import {
  EnumEventAccessOperatorStatus,
  EventManyAppQuery,
} from "gql/generated";
import { EventKey } from "./EventAppView.config";
import { format } from "date-fns";
import { getMultilingualString } from "shared/utils";

/**
 * Filter events that have not passed
 * @param events
 * @returns
 */
const filterNotPassed = (events: EventManyAppQuery["eventMany"]) => {
  const now = format(new Date(), "yyyy-MM-dd");
  return events.filter(
    (event) => (event.recurrencePattern.endDate || event.endDate || "") >= now,
  );
};

/**
 * Filter events that are NOT passed and are approved
 * @param events
 * @param organizationId
 * @returns
 */
const filterApproved = (
  events: EventManyAppQuery["eventMany"],
  organizationId: string,
) => {
  return filterNotPassed(events).filter(
    (event) =>
      event.access.organizations.find(
        (org) => org?._entityId === organizationId,
      )?.status === EnumEventAccessOperatorStatus.Approved,
  );
};

/**
 * Filter events that are NOT passed and are for review
 * @param events
 * @returns
 */
const filterForReview = (
  events: EventManyAppQuery["eventMany"],
  organizationId: string,
) => {
  return filterNotPassed(events).filter(
    (event) =>
      event.access.organizations.find(
        (org) => org?._entityId === organizationId,
      )?.status === EnumEventAccessOperatorStatus.ForReview,
  );
};

/**
 * Filter events that are cancelled
 * @param events
 * @returns
 */
const filterCancelled = (
  events: EventManyAppQuery["eventMany"],
  organizationId: string,
) => {
  return filterNotPassed(events).filter(
    (event) =>
      event.access.organizations.find(
        (org) => org?._entityId === organizationId,
      )?.status === EnumEventAccessOperatorStatus.Cancelled,
  );
};

/**
 * Filter events that are declined
 * @param events
 * @returns
 */
const filterDeclined = (
  events: EventManyAppQuery["eventMany"],
  organizationId: string,
) => {
  return filterNotPassed(events).filter(
    (event) =>
      event.access.organizations.find(
        (org) => org?._entityId === organizationId,
      )?.status === EnumEventAccessOperatorStatus.Declined,
  );
};

/**
 * Filter events that are NOT passed and are for drafts
 * @param events
 * @returns
 */
const filterForDrafts = (
  events: EventManyAppQuery["eventMany"],
  organizationId: string,
) => {
  return filterNotPassed(events).filter(
    (event) =>
      event.access.organizations.find(
        (org) => org?._entityId === organizationId,
      )?.status === EnumEventAccessOperatorStatus.Draft,
  );
};

/**
 * Filter events that have passed
 * @param events
 * @returns
 */
const filterPast = (events: EventManyAppQuery["eventMany"]) => {
  const now = format(new Date(), "yyyy-MM-dd");
  return events.filter(
    (event) => (event.recurrencePattern.endDate || event.endDate || "") < now,
  );
};

/**
 * Filter events by search value
 * @param events
 * @param searchValue
 */
const filterEventsBySearchValue = (
  events: EventManyAppQuery["eventMany"],
  searchValue?: string,
) => {
  searchValue = searchValue?.toLowerCase().trim() ?? "";
  return events.filter((event) =>
    getMultilingualString(event.title).toLowerCase().includes(searchValue),
  );
};

export const getFilteredEvents = (
  events: EventManyAppQuery["eventMany"],
  key: string,
  organizationId: string,
  searchValue?: string,
) => {
  let filteredWidgets = events;
  switch (key) {
    case EventKey.All:
      filteredWidgets = filterNotPassed(events);
      break;
    case EventKey.Approved:
      filteredWidgets = filterApproved(events, organizationId);
      break;
    case EventKey.ForReview:
      filteredWidgets = filterForReview(events, organizationId);
      break;
    case EventKey.Cancelled:
      filteredWidgets = filterCancelled(events, organizationId);
      break;
    case EventKey.Declined:
      filteredWidgets = filterDeclined(events, organizationId);
      break;
    case EventKey.Drafts:
      filteredWidgets = filterForDrafts(events, organizationId);
      break;
    case EventKey.Past:
      filteredWidgets = filterPast(events);
      break;
    default:
      filteredWidgets = [];
  }
  if (searchValue) {
    filteredWidgets = filterEventsBySearchValue(filteredWidgets, searchValue);
  }
  return filteredWidgets;
};
